import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../contexts/LanguageContext';

/**
 * Generic dialog component for entering reason (rejection/return)
 * @param {boolean} visible - Dialog visibility
 * @param {function} onHide - Function to hide dialog
 * @param {function} onConfirm - Function to confirm with reason
 * @param {string} type - Type of dialog ('reject' or 'return')
 */
const ReasonDialog = ({ visible, onHide, onConfirm, type = 'reject' }) => {
    const { th } = useLanguage();
    const [reason, setReason] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Dialog configuration based on type
    const dialogConfig = {
        reject: {
            title: th('preApproval.reasonDialog.reject.title'),
            label: th('preApproval.reasonDialog.reject.label'),
            confirmButton: th('preApproval.reasonDialog.reject.confirmButton'),
            minLength: 20,
            minLengthMessage: th('preApproval.reasonDialog.reject.minLengthMessage'),
            requiredMessage: th('preApproval.reasonDialog.reject.requiredMessage'),
            placeholder: th('preApproval.reasonDialog.reject.placeholder'),
            minLengthText: th('preApproval.reasonDialog.reject.minLengthText')
        },
        return: {
            title: th('preApproval.reasonDialog.return.title'),
            label: th('preApproval.reasonDialog.return.label'),
            confirmButton: th('preApproval.reasonDialog.return.confirmButton'),
            minLength: 20,
            minLengthMessage: th('preApproval.reasonDialog.return.minLengthMessage'),
            requiredMessage: th('preApproval.reasonDialog.return.requiredMessage'),
            placeholder: th('preApproval.reasonDialog.return.placeholder'),
            minLengthText: th('preApproval.reasonDialog.return.minLengthText')
        }
    };

    const config = dialogConfig[type] || dialogConfig.reject;

    const handleConfirm = () => {
        setSubmitted(true);

        // Validate minimum length
        if (reason && reason.trim().length >= config.minLength) {
            onConfirm(reason);
            setReason('');
            setSubmitted(false);
            onHide();
        }
    };

    const handleCancel = () => {
        setReason('');
        setSubmitted(false);
        onHide();
    };

    const renderFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('preApproval.reasonDialog.cancel')} icon="pi pi-times" className="p-button-outlined" onClick={handleCancel} />
                <Button label={config.confirmButton} icon="pi pi-check" onClick={handleConfirm} className={type === 'reject' ? 'p-button-danger' : 'p-button-warning'} />
            </div>
        );
    };

    return (
        <Dialog header={config.title} visible={visible} style={{ width: '450px' }} modal footer={renderFooter()} onHide={handleCancel}>
            <div className="field">
                <label htmlFor="reason" className="font-bold block mb-2">
                    {config.label} ({config.minLengthText.replace('{0}', config.minLength)})
                </label>
                <InputTextarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={5}
                    cols={30}
                    className={submitted && (!reason || reason.trim().length < config.minLength) ? 'p-invalid w-full' : 'w-full'}
                    autoFocus
                    placeholder={config.placeholder}
                />
                {submitted && !reason && <small className="p-error">{config.requiredMessage}</small>}
                {submitted && reason && reason.trim().length < config.minLength && <small className="p-error">{config.minLengthMessage}</small>}
                <div className="mt-2">
                    <small className="text-secondary">
                        {th('preApproval.reasonDialog.characterCount')
                            .replace('{0}', reason ? reason.length : 0)
                            .replace('{1}', config.minLength)}
                    </small>
                </div>
            </div>
        </Dialog>
    );
};

export default ReasonDialog;
